import { buildCostReportAnalyzePrompt } from 'features/cost-report/utils/buildCostReportAnalyzePrompt';
import { useCostReportTimeRangeSelection } from 'features/cost-report/hooks/useCostReportTimeRangeSelection';

import { ChartMoney } from './_components/ChartMoney';
import { ChartTitle } from './_components/ChartTitle';
import { makeChartTooltip } from './_components/ChartTooltip';
import { CostPerProvisionedResourceChart } from './_components/CostPerProvisionedResourceChart';
import {
  DataPointMapper,
  useNormalizedResourceCostChartData,
} from './_hooks/useNormalizedResourceCostChartData';

import { useCluster } from '@cast/shared-cluster-context-data-access';

const chartDataMapper: DataPointMapper = ({
  normalizedCostPerCpu,
  onDemandCostPerCpu,
  spotCostPerCpu,
  fallbackCostPerCpu,
}) => ({
  normalizedCost: normalizedCostPerCpu,
  onDemandCost: onDemandCostPerCpu,
  spotCost: spotCostPerCpu,
  fallbackCost: fallbackCostPerCpu,
});

type Props = { costType: string };

export const CpuCostChart = ({ costType }: Props) => {
  const { cluster } = useCluster();
  const { getRange } = useCostReportTimeRangeSelection();
  const { isLoading, data } =
    useNormalizedResourceCostChartData(chartDataMapper);
  const { tooltipHeader, tooltipFooter } = makeChartTooltip({ unit: 'CPU' });

  return (
    <CostPerProvisionedResourceChart
      header={
        <ChartTitle
          title={`${costType} CPU COST`}
          infoTooltip="Total daily CPU spend divided by the provisioned CPU"
          cost={
            <ChartMoney mapper={({ hourlyCostPerCpu }) => hourlyCostPerCpu} />
          }
        />
      }
      tooltipHeader={tooltipHeader}
      tooltipFooter={tooltipFooter}
      data={data}
      loading={isLoading}
      testId="cost-per-provisioned-resource-chart-cpu"
      buildPrompt={buildCostReportAnalyzePrompt({
        scopeName: cluster?.name,
        context: 'CPU cost per provisioned resource',
        getDefaultRange: getRange,
      })}
    />
  );
};
